import { useEffect, useState } from "react";
import {
  FaBriefcase,
  FaBullseye,
  FaChartLine,
  FaClock,
  FaCloud,
  FaCogs,
  FaDatabase,
  FaGraduationCap,
  FaHandshake,
  FaNetworkWired,
  FaPhone,
  FaShieldAlt,
  FaUserGraduate,
} from "react-icons/fa";
import { CONTACT_PHONE_TEL } from "../data/contactConfig";
import "../styles/CourseDetail.css";

const featureIcons = [FaBullseye, FaGraduationCap, FaBriefcase, FaClock, FaHandshake, FaUserGraduate];
const toolIcons = [FaCogs, FaDatabase, FaNetworkWired, FaShieldAlt, FaChartLine];

export default function CourseDetail({ course, navigate, scrollTarget }) {
  const [openModule, setOpenModule] = useState(0);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", program: course.title });

  useEffect(() => {
    if (!scrollTarget) return;
    const id = scrollTarget.replace("#", "");
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [scrollTarget, course.slug]);

  const handleSubmit = (e) => {
    e.preventDefault();
    window.open(course.formLink, "_blank");
  };

  return (
    <div className="course-detail">
      <section className="cd-hero">
        <div className="cd-hero-inner">
          <div className="cd-hero-text">
            <h1>{course.title}</h1>
            <p>{course.description}</p>
            <div className="cd-hero-actions">
              <a href={`tel:${CONTACT_PHONE_TEL}`} className="cd-btn cd-btn-primary">
                <FaPhone /> Talk to Advisor
              </a>
              <button
                type="button"
                className="cd-btn cd-btn-outline"
                onClick={() => document.getElementById("syllabus")?.scrollIntoView({ behavior: "smooth" })}
              >
                View Syllabus
              </button>
              <button
                type="button"
                className="cd-btn cd-btn-gradient"
                onClick={() => document.getElementById("enroll")?.scrollIntoView({ behavior: "smooth" })}
              >
                Book Demo
              </button>
            </div>
          </div>
          <div className="cd-hero-media">
            <img src={course.image} alt={course.title} />
          </div>
        </div>
      </section>

      <section className="cd-stats">
        <div className="cd-stats-grid">
          {course.stats.map((s) => (
            <div key={s.label} className="cd-stat-card">
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="cd-section">
        <h2 className="cd-heading">Key Features</h2>
        <div className="cd-features-grid">
          {course.features.map((f, i) => {
            const Icon = featureIcons[i] || FaCloud;
            return (
              <div key={f} className="cd-feature-card">
                <Icon />
                <h3>{f}</h3>
              </div>
            );
          })}
        </div>
      </section>

      <section className="cd-section cd-alt">
        <h2 className="cd-heading">Course Overview</h2>
        <div className="cd-overview-grid">
          {course.overview.map((item) => (
            <div key={item.title} className="cd-overview-card">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="cd-section">
        <h2 className="cd-heading">Key Skills You&apos;ll Master</h2>
        <div className="cd-skills-grid">
          {course.skills.map((s) => (
            <div key={s.title} className="cd-skill-card">
              <strong>{s.title}</strong>
              <p>{s.subtitle}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="cd-section cd-alt">
        <h2 className="cd-heading">Essential Tools You&apos;ll Learn</h2>
        <div className="cd-tools-grid">
          {course.tools.map((group, i) => {
            const Icon = toolIcons[i] || FaCogs;
            return (
              <div key={group.title} className="cd-tool-card">
                <Icon className="cd-tool-icon" />
                <h3>{group.title}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section id="syllabus" className="cd-section">
        <h2 className="cd-heading">Course Syllabus</h2>
        <div className="cd-syllabus">
          {course.syllabus.map((mod, i) => (
            <div key={mod.title} className={`cd-module ${openModule === i ? "open" : ""}`}>
              <button type="button" onClick={() => setOpenModule(openModule === i ? null : i)}>
                <span>{mod.title}</span>
                <span className="cd-chevron">{openModule === i ? "▲" : "▼"}</span>
              </button>
              {openModule === i && (
                <ul className="cd-module-body">
                  {mod.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="cd-section cd-alt">
        <h2 className="cd-heading">Career Services &amp; Support</h2>
        <div className="cd-career-banner">
          <FaBullseye />
          <div>
            <strong>100% Placement Assistance Guarantee</strong>
            <p>We don&apos;t just train you — we ensure you land your dream job in the cloud industry.</p>
          </div>
        </div>
        <div className="cd-career-grid">
          {course.careerServices.map((s) => (
            <div key={s.title} className="cd-career-card">
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="enroll" className="cd-section cd-enroll">
        <div className="cd-enroll-inner">
          <div className="cd-enroll-copy">
            <h2>
              Enroll for <span>Bright Career</span>
            </h2>
            <p>Fill in your details and our team will reach out with batch schedules, fees, and demo session slots.</p>
          </div>
          <form className="cd-enroll-form" onSubmit={handleSubmit}>
            <input placeholder="Enter first name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
            <input placeholder="Enter last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
            <input type="email" placeholder="Enter email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input placeholder="Enter contact number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <select value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })}>
              <option value={course.title}>{course.title}</option>
            </select>
            <button type="submit" className="cd-btn cd-btn-primary cd-btn-full">
              Submit
            </button>
          </form>
        </div>
        <button type="button" className="cd-back-link" onClick={() => navigate("/courses")}>
          ← Back to all courses
        </button>
      </section>
    </div>
  );
}
